import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Edit2, Trash2 } from 'lucide-react';

const emptyForm = () => ({ property_id: '', first_name: '', last_name: '', email: '', phone: '', address: '' });

export default function RentersPage() {
  const [renters, setRenters] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [r, p] = await Promise.all([api.get('/renters'), api.get('/properties')]);
    setRenters(r.data);
    setProperties(p.data);
  };

  useEffect(() => { load().catch(() => toast.error('Fehler')).finally(() => setLoading(false)); }, []);

  const getPropertyAddress = (id) => properties.find(p => p.id === id)?.address || '–';
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = renters.filter(r => !normalizedSearch || [
    `${r.first_name} ${r.last_name}`, r.email, r.phone, r.address, getPropertyAddress(r.property_id),
  ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch)));

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDlgOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ property_id: r.property_id || '', first_name: r.first_name, last_name: r.last_name, email: r.email || '', phone: r.phone || '', address: r.address || '' }); setDlgOpen(true); };

  const handleSave = async () => {
    if (!form.first_name || !form.last_name) { toast.error('Name ist pflicht'); return; }
    if (!form.property_id) { toast.error('Liegenschaft ist pflicht'); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/renters/${editing.id}`, form); toast.success('Aktualisiert'); }
      else { await api.post('/renters', form); toast.success('Mieter erstellt'); }
      setDlgOpen(false);
      await load();
    } catch { toast.error('Fehler'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    await api.delete(`/renters/${id}`);
    toast.success('Gelöscht');
    await load();
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="renters-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Mieter</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{renters.length} Mieter</p>
          </div>
          <Button onClick={openNew} data-testid="new-renter-btn" className="rounded-sm gap-2">
            <Plus className="w-4 h-4" />Neuer Mieter
          </Button>
        </div>
        <div className="relative mb-4 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Mieter suchen..." className="pl-9 rounded-sm" data-testid="renter-search" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <div className="space-y-1.5">
            {filtered.map(r => (
              <div key={r.id} data-testid={`renter-row-${r.id}`} className="flex items-center justify-between p-4 bg-card border border-border/60 rounded-sm hover:border-primary/30 hover:shadow-sm transition-all stagger-item">
                <div className="min-w-0">
                  <p className="font-medium text-sm text-foreground">{r.first_name} {r.last_name}</p>
                  <p className="text-xs text-muted-foreground">{getPropertyAddress(r.property_id)}</p>
                  {r.email && <p className="text-xs text-muted-foreground">{r.email}</p>}
                </div>
                <div className="flex items-center gap-2">
                  {r.phone && <span className="text-xs text-muted-foreground font-mono hidden md:block">{r.phone}</span>}
                  <button onClick={() => openEdit(r)} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-sm hover:bg-muted">
                    <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                  <button onClick={() => handleDelete(r.id)} className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-sm hover:bg-destructive/10" data-testid={`delete-renter-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
            {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Keine Mieter gefunden</p>}
          </div>
        )}
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="renter-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Mieter bearbeiten' : 'Neuer Mieter'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label>Liegenschaft *</Label>
              <Select value={form.property_id} onValueChange={v => setForm(f => ({ ...f, property_id: v }))}>
                <SelectTrigger className="rounded-sm" data-testid="renter-property-select"><SelectValue placeholder="Liegenschaft auswählen..." /></SelectTrigger>
                <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Vorname *</Label><Input data-testid="renter-firstname" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Nachname *</Label><Input data-testid="renter-lastname" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="rounded-sm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm" data-testid="save-renter-btn">{saving ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
